<template>
  <header :class="{ 'hide-header': isHidden, 'show-header': !isHidden }">
    <div class="container">
      <!-- ✅ Logo -->
      <div class="logo">
        <router-link to="/" class="logo-link">
          <picture v-if="theme === 'light'">
            <source srcset="/images/logo.webp" type="image/webp" />
            <img src="/images/logo.png" alt="Al-Furqan Logo" />
          </picture>
          <picture v-else>
            <source srcset="/images/logo_dark.webp" type="image/webp" />
            <img src="/images/logo_dark.png" alt="Al-Furqan Logo Dark" />
          </picture>
          <span class="site-name">Al-Furqan</span>
        </router-link>
      </div>

      <!-- ✅ Navigation -->
      <nav class="navigation">
        <router-link to="/" exact-active-class="active-link">home</router-link>
        <router-link to="/about" exact-active-class="active-link"
          >about us</router-link
        >
        <div
          class="courses-links"
          ref="coursesTab"
          @click.stop="toggleDropdown"
        >
          courses
        </div>
        <router-link to="/plans" exact-active-class="active-link"
          >plans</router-link
        >
        <router-link to="/contact-us" exact-active-class="active-link"
          >contact us</router-link
        >

        <!-- ✅ Theme Toggle -->
        <button class="theme-toggle" @click="toggleTheme">
          <font-awesome-icon :icon="['fas', 'circle-half-stroke']" />
        </button>
      </nav>
    </div>

    <!-- ✅ Dropdown Menu -->
    <transition name="slide-fade">
      <ul
        v-if="isDropdown"
        class="dropdown-menu"
        :style="{
          top: dropdownPosition.top + 'px',
          left: dropdownPosition.left + 'px',
        }"
        v-click-outside="closeDropdown"
      >
        <span class="close-button" @click.stop="closeDropdown">×</span>
        <li
          v-for="course in courseNames"
          :key="course.id"
          @click="selectCourseAndClose(course.slug)"
        >
          <router-link :to="`/courses/${course.slug}`">{{
            course.title
          }}</router-link>
        </li>
      </ul>
    </transition>
  </header>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import throttle from "lodash/throttle";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

export default {
  name: "HeaderComponent",
  components: { FontAwesomeIcon },
  data() {
    return {
      isDropdown: false,
      isHidden: false,
      lastScrollY: 0,
      dropdownPosition: { top: 0, left: 0 },
      theme: "light",
    };
  },
  computed: {
    ...mapGetters(["courseNames"]),
  },
  mounted() {
    this.throttledScroll = throttle(this.handleScroll, 200);
    window.addEventListener("scroll", this.throttledScroll);

    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) {
      this.theme = savedTheme;
    }
    document.documentElement.classList.add(this.theme + "-mode");
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.throttledScroll);
  },
  methods: {
    ...mapActions(["selectCourse"]),
    toggleDropdown() {
      if (!this.isDropdown) {
        const rect = this.$refs.coursesTab.getBoundingClientRect();
        this.dropdownPosition.top = rect.bottom + 10;
        this.dropdownPosition.left = rect.left;
      }
      this.isDropdown = !this.isDropdown;
    },
    closeDropdown() {
      this.isDropdown = false;
    },
    selectCourseAndClose(id) {
      this.selectCourse(id);
      this.closeDropdown();
    },
    handleScroll() {
      const currentScrollY = window.scrollY;
      this.isHidden = currentScrollY > this.lastScrollY && currentScrollY > 100;
      this.lastScrollY = currentScrollY;
    },
    toggleTheme() {
      const root = document.documentElement;
      root.classList.remove(this.theme + "-mode");
      this.theme = this.theme === "dark" ? "light" : "dark";
      root.classList.add(this.theme + "-mode");
      localStorage.setItem("theme", this.theme);
    },
  },
};
</script>

<style lang="scss" scoped>
header {
  position: fixed;
  top: 0;
  width: 100%;
  background: var(--background);
  border-bottom: 1px solid var(--link);
  box-shadow: 0 0 10px var(--primary);
  z-index: 1000;
  transition: transform 0.3s ease-in-out;

  &.hide-header {
    transform: translateY(-100%);
  }
  &.show-header {
    transform: translateY(0);
  }

  .container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 55px;
    padding: 0 1rem;

    .logo-link {
      display: flex;
      align-items: center;
      gap: 0.5em;
      text-decoration: none;
      color: #31b553;

      img {
        width: 65px;
        height: auto;
      }

      .site-name {
        font-family: "Merriweather", serif; /* ✅ the new font */
        font-size: 1.6rem;
        font-weight: bold;
        color: #31b553; /* ✅ the primary color */
      }
    }
  }

  nav.navigation {
    display: flex;
    height: 100%;

    a,
    .courses-links,
    .theme-toggle {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 1rem;
      height: 100%;
      color: var(--link);
      text-decoration: none;
      font-weight: normal;
      text-transform: capitalize;
      transition: background 0.3s ease;
      cursor: pointer;

      &:hover {
        background: rgba(49, 181, 83, 0.15);
      }
    }

    .active-link {
      background: #31b553;
      color: #fff !important;
    }

    .theme-toggle {
      border: none;
      background: transparent;
      font-size: 1.4rem;
      cursor: pointer;
    }
  }

  /* ✅ for mobile */
  @media (max-width: 768px) {
    .container {
      flex-direction: column;
      height: auto;

      .logo {
        margin-bottom: 0.5rem;
      }

      nav.navigation {
        flex-wrap: nowrap;
        overflow-x: auto;
        gap: 0.3rem;

        a,
        .courses-links,
        .theme-toggle {
          font-size: 0.8rem;
          padding: 0.3rem 0.6rem;
        }
      }
    }
  }

  /* ✅ for medium screens (tablets) */
  @media (min-width: 769px) and (max-width: 1024px) {
    .container {
      justify-content: space-around; /* ✅ the space between the logo and the nav */
      padding: 0 2rem; /* ✅ increasing the padding */

      .logo-link img {
        width: 55px; /* ✅ smaller for tablets */
      }

      .logo-link .site-name {
        font-size: 1.4rem; /* ✅ suitable for medium screens */
      }

      nav.navigation a,
      nav.navigation .courses-links,
      nav.navigation .theme-toggle {
        padding: 0 0.8rem; /* ✅ reduce the padding */
        font-size: 0.9rem;
      }
    }
  }
}

/* ✅ Dropdown Menu */
.dropdown-menu {
  position: fixed;
  background: var(--surface);
  padding: 1.2em;
  display: flex;
  flex-direction: column;
  gap: 0.8em;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--primary);
  border-radius: 8px;
  z-index: 2000;

  li {
    list-style: none;

    a {
      color: var(--link);
      padding: 0.6em 1em;
      display: block;
      transition: background 0.3s;
      text-decoration: none; /* ✅ remove the underline */

      &:hover {
        background: #31b553;
        color: #fff;
        border-radius: 6px;
      }
    }
  }

  .close-button {
    position: absolute;
    top: 8px;
    right: 10px;
    background: #31b553;
    color: #fff;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1em;
    cursor: pointer;
    transition: background 0.3s;

    &:hover {
      background: var(--accent);
    }
  }
}

/* ✅ تعديل الموبايل */
@media (max-width: 768px) {
  .dropdown-menu {
    left: 0 !important;
    width: 100% !important; /* ✅ the full width */
    border-radius: 0; /* ✅ flat shape */
    text-align: center; /* ✅ center the content */
    padding: 1em 0;

    li a {
      font-size: 1rem;
      padding: 1em; /* ✅big space for mobile */
    }
  }
}

/* ✅ Animation */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}
.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-10%);
}
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10%);
}
</style>
